import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Skeleton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Star as StarIcon,
  Lock as LockIcon,
  CreditCard as CreditCardIcon,
  LocalOffer as PromoIcon,
  Celebration as CelebrationIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import SubscriptionService from '../../services/subscriptionService';

const PREMIUM_FEATURES = [
  'Full access to all Bettor Picks',
  'Unblurred Featured Picks with analysis',
  'Expert handicapper insights',
  'Real-time pick notifications',
  'Parlay Builder — build de-vigged parlays with true-chance pricing across books',
  'Bet Tracker — track every bet, auto-graded, with record/ROI stats',
  'Priority customer support'
];

const PRICE = '$9.99';
const PERIOD = 'month';

const SubscriptionScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { currentUser } = useAuth();
  const { 
    isPremium, 
    subscriptionStatus, 
    currentPeriodEnd, 
    isTrialing,
    trialEnd,
    cancelAtPeriodEnd,
    loading: subscriptionLoading 
  } = useSubscription();

  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Check for success/cancel URL params
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setShowSuccess(true);
      SubscriptionService.logSubscriptionStarted('premium', false);
      // Clear the URL params
      window.history.replaceState({}, '', '/subscription');
    }
    if (searchParams.get('canceled') === 'true') {
      setError('Checkout was canceled. You can try again when ready.');
      window.history.replaceState({}, '', '/subscription');
    }
  }, [searchParams]);

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      setPromoError('Please enter a promo code');
      return;
    }

    setPromoError('');
    setPromoApplied(true);
    // The actual validation happens at Stripe checkout
    // This provides immediate feedback to the user
  };

  const handleSubscribe = async () => {
    try {
      setCheckoutLoading(true);
      setError(null);

      const { url } = await SubscriptionService.createCheckoutSession(
        promoApplied ? promoCode : null
      );

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setPortalLoading(true);
      setError(null);
      await SubscriptionService.openCustomerPortal();
      // Will redirect to Stripe portal
    } catch (err) {
      console.error('Portal error:', err);
      setError(err.message || 'Failed to open billing portal. Please try again.');
      setPortalLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Auth guard - require login to subscribe
  if (!currentUser) {
    return (
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">Premium Subscription</Typography>
        </Box>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <LoginIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Sign In Required
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Please sign in to your account to subscribe to Premium and unlock all features.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<LoginIcon />}
              onClick={() => navigate('/login', { state: { from: '/subscription' } })}
              sx={{ mr: 2 }}
            >
              Sign In
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/login?signup=true', { state: { from: '/subscription' } })}
            >
              Create Account
            </Button>
          </CardContent>
        </Card>

        {/* Still show premium features for non-logged in users */}
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <StarIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h5" gutterBottom>
                Premium Benefits
              </Typography>
              <Typography variant="h3" color="primary" fontWeight="bold">
                {PRICE}
                <Typography component="span" variant="h6" color="text.secondary">
                  /{PERIOD}
                </Typography>
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <List dense>
              {PREMIUM_FEATURES.map((feature, index) => (
                <ListItem key={index} disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Loading state
  if (subscriptionLoading) {
    return (
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">Premium Subscription</Typography>
        </Box>
        <Card>
          <CardContent>
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
            <Skeleton variant="rectangular" height={50} sx={{ mt: 2 }} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">Premium Subscription</Typography>
      </Box>

      {/* Success Alert */}
      {showSuccess && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          icon={<CelebrationIcon />}
          onClose={() => setShowSuccess(false)}
        >
          Welcome to Premium! You now have full access to all features.
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Active Subscription Card */}
      {isPremium && (
        <Card sx={{ mb: 2, border: '2px solid', borderColor: 'primary.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <StarIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" color="primary">
                Premium Active
              </Typography>
              {isTrialing && (
                <Chip 
                  label="Trial" 
                  size="small" 
                  color="secondary" 
                  sx={{ ml: 1 }} 
                />
              )}
              {cancelAtPeriodEnd && (
                <Chip 
                  label="Canceling" 
                  size="small" 
                  color="warning" 
                  sx={{ ml: 1 }} 
                />
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <List dense disablePadding>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ScheduleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Status"
                  secondary={
                    <Chip 
                      label={subscriptionStatus?.replace('_', ' ').toUpperCase() || 'Active'} 
                      size="small" 
                      color={subscriptionStatus === 'active' || subscriptionStatus === 'trialing' ? 'success' : 'default'}
                    />
                  }
                />
              </ListItem>

              {isTrialing && trialEnd && (
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <PromoIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Trial Ends"
                    secondary={formatDate(trialEnd)}
                  />
                </ListItem>
              )}

              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CreditCardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary={cancelAtPeriodEnd ? 'Access Ends' : 'Next Billing Date'}
                  secondary={formatDate(currentPeriodEnd)}
                />
              </ListItem>
            </List>

            {cancelAtPeriodEnd && (
              <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningIcon />}>
                Your subscription will end on {formatDate(currentPeriodEnd)}. 
                You'll still have access until then.
              </Alert>
            )}

            <Button
              variant="outlined"
              fullWidth
              sx={{ mt: 2 }}
              onClick={handleManageSubscription}
              disabled={portalLoading}
              startIcon={portalLoading ? <CircularProgress size={16} /> : <CreditCardIcon />}
            >
              {portalLoading ? 'Opening Portal...' : 'Manage Subscription'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Premium Features Card (always shown) */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {!isPremium && (
            <>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <StarIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h5" gutterBottom>
                  Unlock Premium
                </Typography>
                <Typography variant="h3" color="primary" fontWeight="bold">
                  {PRICE}
                  <Typography component="span" variant="h6" color="text.secondary">
                    /{PERIOD}
                  </Typography>
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />
            </>
          )}

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {isPremium ? 'Your Premium Benefits' : 'What You Get'}
          </Typography>

          <List dense>
            {PREMIUM_FEATURES.map((feature, index) => (
              <ListItem key={index} disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={feature} />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Subscribe Card (only for non-premium users) */}
      {!isPremium && (
        <Card>
          <CardContent>
            {/* Promo Code Section */}
            <Typography variant="subtitle2" gutterBottom>
              Have a promo code?
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder="Enter code"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value.toUpperCase());
                  setPromoApplied(false);
                  setPromoError('');
                }}
                disabled={checkoutLoading}
                error={!!promoError}
                helperText={promoError}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: <PromoIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
              <Button
                variant="outlined"
                onClick={handleApplyPromo}
                disabled={checkoutLoading || promoApplied}
              >
                {promoApplied ? <CheckIcon color="success" /> : 'Apply'}
              </Button>
            </Box>

            {promoApplied && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Promo code applied! It will be validated at checkout.
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Subscribe Button */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleSubscribe}
              disabled={checkoutLoading}
              startIcon={checkoutLoading ? <CircularProgress size={20} color="inherit" /> : <LockIcon />}
              sx={{ py: 1.5 }}
            >
              {checkoutLoading ? 'Redirecting to Checkout...' : 'Subscribe Now'}
            </Button>

            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ display: 'block', textAlign: 'center', mt: 2 }}
            >
              Cancel anytime. Secure payment powered by Stripe.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Free Trial Promo Banner (for non-premium users) */}
      {!isPremium && (
        <Card sx={{ mt: 2, bgcolor: 'secondary.light' }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <CelebrationIcon sx={{ color: 'secondary.dark', mb: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold" color="secondary.dark">
              Try Premium Free for 7 Days!
            </Typography>
            <Typography variant="body2" color="secondary.dark">
              Use promo code <strong>BETTOR7</strong> at checkout
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SubscriptionScreen;
